import { useState, useMemo, useCallback } from 'react';

// mui
import { Box, Button, Container, Divider, Stack } from '@mui/material';

// templates
import { ModalPageTemplate } from '@/pwa/templates/ModalPageTemplate/ModalPageTemplate';
import { PageTemplate } from '@/pwa/templates/PageTemplate/PageTemplate';
import { PageHeader } from '@/pwa/atoms/PageHeader/PageHeader';

// organisms
import { Editor } from '@/components/organisms/Editor/Editor';
import { createFieldHelper } from '@/components/organisms/EditorFields/fieldHelper';
import {
  AccountRole,
  FieldType,
  Organization,
  ReferringPartner,
} from '@karehero/models';

// utils
import { formatRoleName } from '@/utils/format';

const ALLOWED_ROLES = [
  'Member',
  'Organization Owner',
  'Referring Partner Admin',
];

export type VerifyAccountPayload = {
  organizationID?: string;
  roleID: string;
  roleName?: string;
  partnerID?: string;
};

export type VerifyAccountFormProps = {
  accountID: string;
  onSubmit?: (accountID: string, payload: VerifyAccountPayload) => void;
  onBack: () => void;
  accountRoles: AccountRole[];
  organizations: Organization[];
  partners?: ReferringPartner[];
};

export const VerifyAccountForm = ({
  accountID,
  onSubmit,
  onBack,
  accountRoles,
  organizations,
  partners,
}: VerifyAccountFormProps) => {
  const [formState, setFormState] = useState<VerifyAccountPayload | undefined>(
    undefined,
  );

  const selectedRole = useMemo(() => {
    if (!formState?.roleID) return undefined;
    return (accountRoles || []).find((r) => r.id === formState.roleID);
  }, [formState?.roleID, accountRoles]);

  const isUpgrade =
    selectedRole?.name === 'Organization Owner' ||
    selectedRole?.name === 'Referring Partner Admin';

  const fields = useMemo(() => {
    const base: Record<string, any> = {
      roleID: {
        label: 'Account Role',
        isRequired: true,
        field: {
          type: FieldType.Select,
          options: (accountRoles || [])
            .filter((r) => ALLOWED_ROLES.includes(r.name))
            .map((accountRole) => ({
              label: formatRoleName(accountRole.name),
              value: accountRole.id,
            })),
        },
      },
    };

    if (
      !selectedRole ||
      selectedRole.name === 'Member' ||
      selectedRole.name === 'Organization Owner'
    ) {
      base.organizationID = {
        label: 'Organisation',
        isRequired:
          !selectedRole || selectedRole.name !== 'Referring Partner Admin',
        field: {
          type: FieldType.Select,
          options: (organizations || []).map((organization) => ({
            label: organization.name,
            value: organization.id,
          })),
        },
      };
    }

    if (selectedRole?.name === 'Referring Partner Admin') {
      base.partnerID = {
        label: 'Referring Partner',
        isRequired: true,
        field: {
          type: FieldType.Select,
          options: (partners || []).map((partner) => ({
            label: partner.name,
            value: partner.id,
          })),
        },
      };
    }

    return createFieldHelper<VerifyAccountPayload>(base);
  }, [accountRoles, organizations, partners, selectedRole]);

  const handleSubmit = useCallback(() => {
    if (!formState) {
      throw new Error('role or organization is not set');
    }

    onSubmit?.(accountID, {
      ...formState,
      roleName: selectedRole?.name,
    });
  }, [onSubmit, accountID, formState, selectedRole]);

  return (
    <ModalPageTemplate
      onBack={onBack}
      footerSlot={
        <Box
          sx={{
            bgcolor: 'background.default',
          }}
        >
          <Divider />
          <Container maxWidth='sm'>
            <Stack
              direction='row'
              spacing={2}
              sx={{
                py: 2,
              }}
            >
              <Button variant='outlined' fullWidth onClick={onBack}>
                Cancel
              </Button>
              <Button variant='contained' fullWidth onClick={handleSubmit}>
                {isUpgrade ? 'Upgrade Role' : 'Verify Account'}
              </Button>
            </Stack>
          </Container>
        </Box>
      }
    >
      <PageTemplate
        maxWidth='sm'
        headerSlot={
          <PageHeader
            title={isUpgrade ? 'Upgrade Account Role' : 'Verify Account'}
            description={
              isUpgrade
                ? 'Upgrade the account to a new role'
                : 'Verify the account'
            }
          />
        }
        contentSlot={
          <Editor
            isActionsDisabled
            isFloatingActions={false}
            isForm={false}
            value={formState}
            onChange={(newValue) => {
              setFormState({ ...formState, ...newValue });
            }}
            fields={fields}
          />
        }
      />
    </ModalPageTemplate>
  );
};

VerifyAccountForm.displayName = 'VerifyAccountForm';

export default VerifyAccountForm;
